import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { LabelTemplate, LabelSheetConfig, FieldData } from '@/types/label';
import type { Json } from '@/integrations/supabase/types';

export interface LabelPrint {
  id: string;
  user_id: string;
  template_id: string;
  template_name: string;
  template_snapshot: LabelTemplate;
  field_data: FieldData;
  copies: number;
  print_source: string;
  original_print_id: string | null;
  sheet_config: LabelSheetConfig | null;
  is_deleted: boolean;
  created_at: string;
  user_agent: string | null;
}

interface SaveLabelPrintParams {
  template: LabelTemplate;
  fieldData: FieldData;
  copies: number;
  printSource: 'editor' | 'kiosk' | 'reprint';
  sheetConfig?: LabelSheetConfig;
  originalPrintId?: string;
}

export const useLabelHistory = () => {
  const { user, isAdmin } = useAuth();
  const [prints, setPrints] = useState<LabelPrint[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const saveLabelPrint = useCallback(async (params: SaveLabelPrintParams): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('label_prints')
        .insert({
          user_id: user.id,
          template_id: params.template.id,
          template_name: params.template.name,
          template_snapshot: JSON.parse(JSON.stringify(params.template)) as unknown as Json,
          field_data: params.fieldData as unknown as Json,
          copies: params.copies,
          print_source: params.printSource,
          original_print_id: params.originalPrintId || null,
          sheet_config: params.sheetConfig ? (JSON.parse(JSON.stringify(params.sheetConfig)) as unknown as Json) : null,
          user_agent: navigator.userAgent,
        })
        .select('id')
        .single();

      if (error) {
        console.error('Failed to save label print:', error);
        return null;
      }
      return data.id;
    } catch (error) {
      console.error('Failed to save label print:', error);
      return null;
    }
  }, [user]);

  const fetchHistory = useCallback(async (filters?: {
    search?: string;
    templateName?: string;
    dateFrom?: string;
    dateTo?: string;
    userId?: string; // admin only
    printSource?: string;
    copiesRange?: string;
    limit?: number;
    offset?: number;
  }) => {
    if (!user) return;
    setLoading(true);

    try {
      let query = supabase
        .from('label_prints')
        .select('*', { count: 'exact' })
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      // Non-admins can only see their own
      if (!isAdmin || !filters?.userId) {
        if (!isAdmin) {
          query = query.eq('user_id', user.id);
        }
      }

      if (filters?.userId && isAdmin) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.templateName) {
        query = query.ilike('template_name', `%${filters.templateName}%`);
      }

      if (filters?.search) {
        const uuidPart = isValidUuid(filters.search) ? `,id.eq.${filters.search}` : '';
        query = query.or(`template_name.ilike.%${filters.search}%,field_data_text.ilike.%${filters.search}%${uuidPart}`);
      }

      if (filters?.printSource && filters.printSource !== 'all') {
        query = query.eq('print_source', filters.printSource);
      }

      if (filters?.copiesRange && filters.copiesRange !== 'all') {
        switch (filters.copiesRange) {
          case '1': query = query.eq('copies', 1); break;
          case '2-5': query = query.gte('copies', 2).lte('copies', 5); break;
          case '6-10': query = query.gte('copies', 6).lte('copies', 10); break;
          case '10+': query = query.gt('copies', 10); break;
        }
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const limit = filters?.limit || 50;
      const offset = filters?.offset || 0;
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('Failed to fetch label history:', error);
        return;
      }

      const mapped: LabelPrint[] = (data || []).map(row => ({
        id: row.id,
        user_id: row.user_id,
        template_id: row.template_id,
        template_name: row.template_name,
        template_snapshot: row.template_snapshot as unknown as LabelTemplate,
        field_data: row.field_data as unknown as FieldData,
        copies: row.copies,
        print_source: row.print_source,
        original_print_id: row.original_print_id,
        sheet_config: row.sheet_config as unknown as LabelSheetConfig | null,
        is_deleted: row.is_deleted,
        created_at: row.created_at,
        user_agent: row.user_agent,
      }));

      setPrints(mapped);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Failed to fetch label history:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  const exportHistory = useCallback(async (filters?: { userId?: string }) => {
    if (!user || !isAdmin) return;

    let query = supabase
      .from('label_prints')
      .select('*')
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(1000);

    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }

    const { data } = await query;
    if (!data) return;

    const csvRows = [
      ['ID', 'Template', 'Copies', 'Source', 'Original Print ID', 'Created At', 'User ID'].join(','),
      ...data.map(row =>
        [row.id, `"${row.template_name}"`, row.copies, row.print_source, row.original_print_id || '', row.created_at, row.user_id].join(',')
      ),
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [user, isAdmin]);

  return {
    prints,
    loading,
    totalCount,
    saveLabelPrint,
    fetchHistory,
    exportHistory,
  };
};

function isValidUuid(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}
